import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { forexApi, type ConfigPackage, type ConfigRelease } from "@/lib/forex-api";

type Toast = { type: "success" | "error"; msg: string };

const policyFields = [
  { key: "recent_order_range_days", label: "最近订单查询范围", unit: "天", fallback: "未配置" },
  { key: "page_size", label: "默认分页大小", unit: "条/页", fallback: "未配置" },
  { key: "timeout_seconds", label: "硬超时阈值", unit: "秒", fallback: "未配置" },
  { key: "cache_strategy", label: "缓存策略", unit: "", fallback: "由服务端默认策略控制" },
  { key: "rate_limit_per_minute", label: "接口频率限制", unit: "req/min", fallback: "由网关限流控制" },
];

function formatTime(timestamp?: number | null) {
  if (!timestamp) return "未发布";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 16);
}

function displayValue(value: unknown, unit: string, fallback: string) {
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "number" && unit) return `${value} ${unit}`;
  return String(value);
}

export default function QueryPolicyTab() {
  const [pkg, setPkg] = useState<ConfigPackage | null>(null);
  const [releases, setReleases] = useState<ConfigRelease[]>([]);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [loadError, setLoadError] = useState<"missing" | "failed" | null>(null);
  const [invalid, setInvalid] = useState("");

  const loadPolicy = () => {
    setLoading(true);
    setLoadError(null);
    setToast(null);
    Promise.all([forexApi.getConfigPackage("query_policy"), forexApi.listConfigReleases()])
      .then(([packageResult, releaseResult]) => {
        setPkg(packageResult);
        setDraft(packageResult.payload);
        setReleases(releaseResult.items.filter((item) => item.config_key === "query_policy"));
      })
      .catch((error: unknown) => {
        const missing = isAxiosError(error) && error.response?.status === 404 && error.response?.data?.detail?.code === "CONFIG_PACKAGE_001";
        setPkg(null); setReleases([]); setDraft({}); setLoadError(missing ? "missing" : "failed");
        setToast({ type: "error", msg: missing ? "尚未创建查询策略配置包；请核对配置来源后再编辑或发布。" : "查询策略加载失败，请确认后端服务或登录态。" });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPolicy();
  }, []);

  const updateDraftNumber = (key: string, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value === "" ? "" : Number(value) }));
  };

  const handleSave = async () => {
    if (!pkg) return;
    const bad = policyFields.find(field => typeof pkg.payload[field.key] === "number" && (draft[field.key] === "" || !Number.isInteger(Number(draft[field.key])) || Number(draft[field.key]) <= 0));
    if (bad) { setInvalid(`${bad.label}必须是大于 0 的整数`); return; }
    setInvalid("");
    setSaving(true);
    try {
      const updated = await forexApi.patchConfigPackage(pkg.config_key, {
        payload: draft,
        description: "查询窗口、分页、缓存、超时和频率策略",
      });
      setPkg(updated);
      setDraft(updated.payload);
      setEditing(false);
      setToast({ type: "success", msg: "查询策略草稿已保存" });
    } catch {
      setToast({ type: "error", msg: "保存结果未知，请刷新核对当前版本后再操作。" });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!pkg) return;
    if (editing) { setToast({ type: "error", msg: "请先保存或取消编辑，再发布当前版本。" }); return; }
    if (!window.confirm(`确认生成查询策略版本 ${pkg.version} 的后台发布记录？C 端实际读取结果需另行核对。`)) return;
    setPublishing(true);
    try {
      const result = await forexApi.publishConfigPackage(pkg.config_key, {
        description: "查询策略后台发布记录",
      });
      setPkg(result.package);
      setDraft(result.package.payload);
      setReleases((prev) => [result.release, ...prev]);
      setToast({ type: "success", msg: "后台发布记录已生成；C 端实际读取结果待核对。" });
    } catch {
      setToast({ type: "error", msg: "发布结果未知，请先核对发布记录与 C 端配置，避免重复发布。" });
    } finally {
      setPublishing(false);
    }
  };

  const latestRelease = releases[0];
  const dirty = pkg && JSON.stringify(draft) !== JSON.stringify(pkg.payload);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">查询策略</h2>
          <p className="text-xs text-text-muted mt-0.5">
            管理后台查询策略记录；具体范围以当前配置值为准。后台发布记录不证明 GET /app/config/bootstrap 已生效。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { if (editing) { setDraft(pkg?.payload || {}); setInvalid(""); } setEditing(prev => !prev); }}
            disabled={!pkg}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand disabled:opacity-50"
          >
            <i className={editing ? "ri-close-line text-sm" : "ri-edit-line text-sm"} />
            {editing ? "取消编辑" : "编辑策略"}
          </button>
          <button
            onClick={handleSave}
            disabled={!editing || saving || !dirty}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand text-white disabled:opacity-50"
          >
            <i className={saving ? "ri-loader-4-line animate-spin text-sm" : "ri-save-line text-sm"} />
            {saving ? "保存中" : "保存草稿"}
          </button>
          <button
            onClick={handlePublish}
            disabled={!pkg || publishing || !!loadError || editing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white disabled:opacity-50"
          >
            <i className={publishing ? "ri-loader-4-line animate-spin text-sm" : "ri-upload-cloud-line text-sm"} />
            {publishing ? "发布中" : "生成发布记录"}
          </button>
          <Link
            to="/forex/query-policy/detail"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand"
          >
            <i className="ri-eye-line text-sm" />详情
          </Link>
        </div>
      </div>

      {toast && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${toast.type === "success" ? "border-emerald-500/20 bg-emerald-500/5 text-emerald-600" : "border-red-500/20 bg-red-500/5 text-red-500"}`}>
          {toast.msg}
        </div>
      )}
      {invalid && <p role="alert" className="text-sm text-red-600">{invalid}</p>}
      {!loading && loadError && <button onClick={loadPolicy} className="text-sm text-brand underline">重新读取查询策略</button>}
      {editing && <p className="text-xs text-amber-600">{dirty ? "有未保存修改；取消编辑会恢复已读取版本。" : "当前没有修改。"}发布仅针对已保存版本。</p>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">配置状态</p>
          <p className={`text-sm font-semibold mt-1 ${pkg?.status === "enabled" ? "text-emerald-500" : "text-text-secondary"}`}>{loading ? "加载中" : loadError === "missing" ? "未创建" : loadError || !pkg ? "不可用" : pkg.status === "enabled" ? "已启用" : "未启用"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">当前版本</p>
          <p className="text-sm font-semibold font-mono mt-1 text-text-primary">{loadError ? "不可用" : pkg?.version || "--"}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">最后发布</p>
          <p className="text-sm font-semibold font-mono mt-1 text-text-primary">{loadError ? "不可用" : formatTime(pkg?.last_published_at)}</p>
        </div>
        <div className="bg-white rounded-lg border border-surface-border px-4 py-3">
          <p className="text-[11px] text-text-muted">发布记录</p>
          <p className="text-sm font-semibold font-mono mt-1 text-brand">{loadError ? "不可用" : releases.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-surface-border bg-surface/50">
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">配置项</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">配置键</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">当前值</th>
                <th className="text-left px-4 py-2.5 text-text-secondary font-medium text-[11px]">影响 API</th>
              </tr>
            </thead>
            <tbody>
              {policyFields.map((field) => (
                <tr key={field.key} className="border-b border-surface-border hover:bg-surface/30 transition-colors">
                  <td className="px-4 py-2.5 text-text-primary text-xs font-medium">{field.label}</td>
                  <td className="px-4 py-2.5"><code className="text-[10px] text-text-muted bg-surface px-1.5 py-0.5 rounded">{field.key}</code></td>
                  <td className="px-4 py-2.5">
                    {editing && typeof pkg?.payload[field.key] === "number" ? (
                      <input
                        type="number"
                        min="1" step="1" aria-label={field.label}
                        value={draft[field.key] as number | string}
                        onChange={(event) => updateDraftNumber(field.key, event.target.value)}
                        className="w-[120px] h-7 rounded border border-brand/30 px-2 text-xs font-mono text-text-primary focus:outline-none focus:border-brand"
                      />
                    ) : (
                      <span className="text-xs font-mono text-text-primary">{loadError === "missing" ? "未配置" : loadError ? "不可用" : displayValue(draft[field.key], field.unit, field.fallback)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-text-muted font-mono text-[10px]">{pkg?.api_path || "GET /app/config/bootstrap"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-surface-border p-4">
        <h3 className="text-sm font-semibold text-text-primary mb-3">最近发布</h3>
        {latestRelease ? (
          <div className="flex items-center justify-between gap-3 rounded-lg bg-surface/50 px-3 py-2">
            <div>
              <p className="text-xs font-medium text-text-primary">{latestRelease.description || "查询策略发布"}</p>
              <p className="text-[10px] text-text-muted font-mono mt-0.5">{latestRelease.version} · {formatTime(latestRelease.published_at)}</p>
            </div>
            <span className="text-[10px] text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded">{latestRelease.status}</span>
          </div>
        ) : (
          <p className="text-xs text-text-muted">暂无发布记录</p>
        )}
      </div>
    </div>
  );
}
